import { useEffect, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import UFOSegment from "@atlaskit/react-ufo/segment";

import { Adg4Button } from "@/components/adg4/button/src";
import { useTemplateFuzzySearchContext } from "@/components/confluence/templates/context/template-fuzzy-search-context.tsx";
import { TemplateCard } from "@/components/confluence/templates/template-card";
import { ConfluenceTemplateMessages } from "@/components/confluence/templates/template-messages.ts";
import { Template } from "@/content/templates/template.types.ts";

export interface TemplateCardGridProps {
    templates: Template[];
}

const gridStyles = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
});

const gridContainerStyles = css({
    "display": "flex",
    "flexWrap": "wrap",
    "listStyle": "none",
    "listStylePosition": "inside",
    "paddingInlineStart": 0,
    "margin": 0,
    "gap": 64,
    "@media (min-width: 40rem) and (max-width: 63.99rem)": {
        width: "676px",
        gap: "32px"
    },
    "justifyContent": "center"
});

const gridItemStyles = css({
    listStyleType: "none",
    display: "flex",
    flexWrap: "wrap",
    listStyle: "none",
    opacity: 0,
    transform: "translate3d(0, 60px, 0)"
});

const inViewAnimation = css({
    opacity: 1,
    transform: "translate3d(0, 0, 0)",
    transition: "opacity .3s ease-in-out, transform .3s ease-in-out"
});

const buttonStyle = css({
    width: "210px",
    height: "44px",
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    gap: "10px",
    padding: "16px"
});

const CARD_FADE_UP_ANIMATION_DELAY = 0.2;

function getFadeUpAnimationDelay(shouldAnimate: boolean, index: number) {
    if (!shouldAnimate) {
        return "0s";
    }

    const calculated = CARD_FADE_UP_ANIMATION_DELAY * index;
    return calculated > 1.2 ? `1.2s` : `${calculated}s`;
}

export const TEMPLATE_CARD_BACKGROUNDS = [
    "url(https://wac-cdn.atlassian.com/misc-assets/webp-images/bg1.svg)",
    "url(https://wac-cdn.atlassian.com/misc-assets/webp-images/bg2.svg)",
    "url(https://wac-cdn.atlassian.com/misc-assets/webp-images/bg3.svg)",
    "url(https://wac-cdn.atlassian.com/misc-assets/webp-images/bg4.svg)",
    "url(https://wac-cdn.atlassian.com/misc-assets/webp-images/bg5.svg)",
    "url(https://wac-cdn.atlassian.com/misc-assets/webp-images/bg6.svg)",
    "url(https://wac-cdn.atlassian.com/misc-assets/webp-images/bg7.svg)",
    "url(https://wac-cdn.atlassian.com/misc-assets/webp-images/bg8.svg)",
    "url(https://wac-cdn.atlassian.com/misc-assets/webp-images/bg9.svg)"
];

export const TemplateCardGrid = ({ templates = [] }: TemplateCardGridProps) => {
    const [shouldAnimate, setShouldAnimate] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const [visibleTemplates, setVisibleTemplates] = useState(
        templates.slice(0, 9)
    );
    const { currentCategory } = useTemplateFuzzySearchContext();

    const loadMoreTemplates = () => setShowAll(true);

    useEffect(() => {
        setTimeout(() => {
            setShouldAnimate(true);
        }, 100);
    }, []);

    useEffect(() => {
        if (typeof templates !== "undefined") {
            setVisibleTemplates(showAll ? templates : templates.slice(0, 9));
        }
    }, [templates, showAll]);

    // Effect hook to reset `showAll` and `visibleTemplates` when `currentCategory` changes
    useEffect(() => {
        setShowAll(false);
        setVisibleTemplates(templates.slice(0, 9)); // Reset to initial state for new category
    }, [currentCategory, templates]);

    return (
        <UFOSegment name="confluence-template-card-grid">
            <div css={gridStyles}>
                {templates.length === 0 ? (
                    <div>
                        <FormattedMessage
                            {...ConfluenceTemplateMessages.noTemplatesAvailableText}
                        />
                    </div>
                ) : (
                    <>
                        <ul css={gridContainerStyles}>
                            {visibleTemplates.map((template, index) => (
                                <li
                                    key={template.templateId}
                                    css={[
                                        gridItemStyles,
                                        shouldAnimate && inViewAnimation
                                    ]}
                                    style={{
                                        transitionDelay: `${getFadeUpAnimationDelay(
                                            shouldAnimate,
                                            index
                                        )}`
                                    }}
                                    data-testid={`template-card-grid-item-${index}`}
                                >
                                    <TemplateCard
                                        template={template}
                                        background={
                                            TEMPLATE_CARD_BACKGROUNDS[
                                                index %
                                                    TEMPLATE_CARD_BACKGROUNDS.length
                                            ]
                                        }
                                    />
                                </li>
                            ))}
                        </ul>

                        {templates?.length > 9 && !showAll && (
                            <Adg4Button
                                variant="secondary"
                                size="small"
                                color={"black"}
                                css={buttonStyle}
                                style={{
                                    marginTop: "60px",
                                    textAlign: "center"
                                }}
                                onClick={loadMoreTemplates}
                                dataEventProps={{
                                    "data-product-key": "confluence",
                                    "data-event": "clicked",
                                    "data-event-component": "linkButton",
                                    "data-event-container": "templatesSection",
                                    "data-label": "load-more-templates-button",
                                    "data-label-english": "Load more templates",
                                    "data-track-event":
                                        "confluence.load-more-templates.cta"
                                }}
                            >
                                <FormattedMessage
                                    {...ConfluenceTemplateMessages.loadMoreTemplatesButtonText}
                                />
                            </Adg4Button>
                        )}
                    </>
                )}
            </div>
        </UFOSegment>
    );
};
